/**
 * Utility for calculating default date ranges based on granularity.
 * These defaults moved from backend API layer to frontend for proper separation of concerns.
 */

/**
 * Default lookback periods (in days) for each granularity.
 * Optimized for focused, readable charts - users can pan or adjust date range if needed.
 */
const DEFAULT_DAYS_BY_GRANULARITY: Record<number, number> = {
  1: 1,        // 1m: 1 day (1440 candles) - keeps current focused view
  5: 2,        // 5m: 2 days (576 candles) - reduced from 3 days for better focus
  15: 3,       // 15m: 3 days (288 candles) - reduced from 7 days, more readable
  30: 3,       // 30m: 3 days (144 candles) - reduced from 7 days, cleaner view
  60: 5,       // 1h: 5 days (120 candles) - reduced from 7 days
  240: 14,     // 4h: 14 days (84 candles) - reduced from 30 days
  1440: 60,    // 1d: 60 days (60 candles) - reduced from 90 days
  10080: 365,  // 1w: 365 days (52 candles) - keeps current annual view
};

/**
 * Calculate default date range for a given granularity.
 * Returns ISO-8601 formatted strings suitable for API calls.
 *
 * @param granularityMins - Candle granularity in minutes
 * @param endTime - Optional end time (defaults to now)
 * @returns Object with startTime and endTime as ISO-8601 strings
 */
export function getDefaultDateRange(
  granularityMins: number,
  endTime?: Date
): { startTime: string; endTime: string } {
  const end = endTime || new Date();
  const days = DEFAULT_DAYS_BY_GRANULARITY[granularityMins] || 7; // Default to 7 days if unknown granularity

  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);

  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}

/**
 * Get the default lookback period in days for a granularity.
 * Useful for display purposes or custom calculations.
 *
 * @param granularityMins - Candle granularity in minutes
 * @returns Number of days to look back
 */
export function getDefaultLookbackDays(granularityMins: number): number {
  return DEFAULT_DAYS_BY_GRANULARITY[granularityMins] || 7;
}

